import React, { useState, useEffect } from 'react';
import { Card, Typography, Space, Spin, Alert, Button, Modal, Form, Input, Select, Row, Col, Radio, Upload, message, TimePicker } from 'antd';
import { PrinterOutlined, FilterOutlined, SettingOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { Participant } from '../types/api';
import { useBiathlonEvent } from '../contexts/BiathlonEventContext';
import dayjs from 'dayjs';

// Recursively finds an attribute in an object
export function findAttribute(obj: any, key: string): any | undefined {
  if (obj && typeof obj === 'object') {
    if (key in obj) return obj[key];
    for (const k in obj) {
      const result = findAttribute(obj[k], key);
      if (result !== undefined) return result;
    }
  }
  return undefined;
}

const { Text, Title } = Typography;

interface ShootingDirectionData {
  [uid: string]: 'V' | 'H';
}

const ShootingDirectionSelector: React.FC<{ participant?: Participant }> = ({ participant }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedDirection, setSelectedDirection] = useState<'V' | 'H' | null>(null);

  const getStoredDirection = (): 'V' | 'H' | null => {
    if (!participant) return null;
    try {
      const stored = localStorage.getItem('ShootingDirection');
      if (stored) {
        const data: ShootingDirectionData = JSON.parse(stored);
        return data[participant.Utover.UID.toString()] || null;
      }
    } catch (error) {
      console.error('Error reading from localStorage:', error);
    }
    return null;
  };

  const saveDirection = (direction: 'V' | 'H') => {
    if (!participant) return;
    try {
      const stored = localStorage.getItem('ShootingDirection');
      const data: ShootingDirectionData = stored ? JSON.parse(stored) : {};
      data[participant.Utover.UID.toString()] = direction;
      localStorage.setItem('ShootingDirection', JSON.stringify(data));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  const storedDirection = getStoredDirection();

  const handleClick = () => {
    if (!participant) return;
    setSelectedDirection(storedDirection);
    setModalVisible(true);
  };

  const handleOk = () => {
    if (selectedDirection && participant) {
      saveDirection(selectedDirection);
      setModalVisible(false);
    }
  };

  const handleCancel = () => {
    setModalVisible(false);
    setSelectedDirection(null);
  };

  const getDisplayText = () => {
    if (storedDirection) {
      return storedDirection;
    }
    return 'V / H';
  };

  const getTextColor = () => {
    if (storedDirection === 'V') return '#ff0000';
    if (storedDirection === 'H') return '#00ff00';
    return '#1890ff';
  };

  const getParticipantName = () => {
    if (!participant) return 'Deltaker';
    return `${participant.Utover.Fornavn} ${participant.Utover.Etternavn}`;
  };

  return (
    <>
      <Text
        style={{
          color: getTextColor(),
          fontWeight: 'bold',
          textDecoration: 'underline',
          fontSize: '18px',
          cursor: participant ? 'pointer' : 'default',
        }}
        onClick={handleClick}
      >
        {getDisplayText()}
      </Text>
      
      {participant && (
        <Modal
          title={`Skyteplott for ${getParticipantName()}`}
          open={modalVisible}
          onOk={handleOk}
          onCancel={handleCancel}
          okText="Lagre"
          cancelText="Avbryt"
        >
          <Form layout="vertical">
            <Form.Item label="Skyter fra venstre eller høyre?">
              <Radio.Group
                value={selectedDirection}
                onChange={(e) => setSelectedDirection(e.target.value)}
              >
                <Radio value="V">Skyter fra venstre (V)</Radio>
                <Radio value="H">Skyter fra høyre (H)</Radio>
              </Radio.Group>
            </Form.Item>
          </Form>
        </Modal>
      )}
    </>
  );
};

const Skyteplott: React.FC = () => {
  const navigate = useNavigate();
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [settingsModalVisible, setSettingsModalVisible] = useState(false);
  const { selectedEvent, athletes, loading, error, clubs, teams, classes, selectedClubs, setSelectedClubs, selectedTeams, setSelectedTeams, selectedClasses, setSelectedClasses, nameFilter, setNameFilter, startTimeFrom, setStartTimeFrom, startTimeTo, setStartTimeTo } = useBiathlonEvent();
  const participants = athletes;

  useEffect(() => {
    if (!selectedEvent) {
      navigate('/');
    }
  }, [selectedEvent, navigate]);

  const handlePrint = () => {
    window.print();
  };

  const handleExportShootingDirections = () => {
    try {
      const shootingDirections = localStorage.getItem('ShootingDirection');
      const data = shootingDirections ? JSON.parse(shootingDirections) : {};
      
      const dataStr = JSON.stringify(data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `shooting-directions-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      message.success('Shooting directions exported successfully!');
    } catch (error) {
      console.error('Error exporting shooting directions:', error);
      message.error('Failed to export shooting directions');
    }
  };

  const handleImportShootingDirections = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const importedData = JSON.parse(content);
        
        if (typeof importedData === 'object' && importedData !== null) {
          const isValid = Object.values(importedData).every(
            (value) => value === 'V' || value === 'H'
          );
          
          if (isValid) {
            localStorage.setItem('ShootingDirection', JSON.stringify(importedData));
            message.success('Shooting directions imported successfully!');
            window.location.reload();
          } else {
            message.error('Invalid file format: values must be "V" or "H"');
          }
        } else {
          message.error('Invalid file format: expected JSON object');
        }
      } catch (error) {
        console.error('Error importing shooting directions:', error);
        message.error('Failed to import shooting directions: invalid JSON file');
      }
    };
    reader.readAsText(file);
    return false;
  };

  const handleFilterOk = () => {
    setFilterModalVisible(false);
  };

  const handleFilterCancel = () => {
    setFilterModalVisible(false);
  };

  const handleFilterReset = () => {
    setSelectedClubs([]);
    setSelectedTeams([]);
    setSelectedClasses([]);
    setNameFilter('');
  };

  const handleSettingsOk = () => {
    setSettingsModalVisible(false);
  };

  const handleSettingsCancel = () => {
    setSettingsModalVisible(false);
  };
  const renderWheel = () => (
    <div
      style={{
        width: '25mm', // Increased by 25% from 20mm
        height: '25mm',
        border: '2px solid #333',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'white',
      }}
    >
      <div
        style={{
          width: '12.5mm', // Increased by 25% from 10mm
          height: '12.5mm',
          border: '1px solid #333',
          borderRadius: '50%',
          backgroundColor: 'white',
        }}
      />
    </div>
  );

  const renderBibBox = (participant?: Participant) => {
    const shouldShowLane = participant && participant.Skive != null;
    
    return (
      <div
        style={{
          width: '24mm',
          height: '12mm',
          border: '2px solid #333',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'white',
          padding: '1mm',
        }}
      >
        <Text style={{ color: '#1890ff', textDecoration: 'underline', fontWeight: 'bold', fontSize: '10px', marginBottom: '1mm' }}>
          {shouldShowLane ? 'Bib/Lane' : 'Bib'}
        </Text>
        {shouldShowLane && (
          <Text style={{ fontSize: '14px', fontWeight: 'bold', textAlign: 'center' }}>
            {participant.Startnummer} / {participant.Skive}
          </Text>
        )}
        {!shouldShowLane && participant && (
          <Text style={{ fontSize: '14px', fontWeight: 'bold', textAlign: 'center' }}>
            {participant.Startnummer}
          </Text>
        )}
      </div>
    );
  };


  const renderParticipantInfo = (participant?: Participant) => {
    const truncateName = (participant: Participant) => {
      const firstName = participant.Utover.Fornavn.split(' ')[0];
      const lastName = participant.Utover.Etternavn;
      const fullName = `${firstName} ${lastName}`;
      
      if (fullName.length <= 25) {
        return fullName;
      }
      
      const maxFirstNameLength = 25 - lastName.length - 1;
      if (maxFirstNameLength > 0) {
        return `${firstName.substring(0, maxFirstNameLength)}... ${lastName}`;
      }
      
      return fullName.substring(0, 25) + '...';
    };

    const getStartTime = (participant: Participant) => {
      if (participant.EtappeDeltaker && (participant.EtappeDeltaker.length > 0 || true)) {
        const _part = participant;
        const _ed = _part.EtappeDeltaker;
        const starttid = findAttribute(_ed, 'StarttidFormatert');
        return starttid || 'Unknown'; 

      }
      return 'Unknown';
    };

    return (
      <div
        style={{
          width: '65mm',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginBottom: '3mm',
        }}
      >
        {participant && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            width: '100%',
            marginBottom: '2mm' 
          }}>
            <Text
              style={{
                color: '#1890ff',
                fontSize: '14px',
                fontWeight: 'bold',
              }}
            >
              {truncateName(participant)}
            </Text>
            <Text
              style={{
                color: '#ff0000',
                fontSize: '14px',
                fontWeight: 'bold',
              }}
            >
              {getStartTime(participant)}
            </Text>
          </div>
        )}
      </div>
    );
  };

  const getFilteredParticipants = () => {
    return participants.filter(participant => {
      if (nameFilter) {
        const fullName = `${participant.Utover.Fornavn} ${participant.Utover.Etternavn}`.toLowerCase();
        if (!fullName.includes(nameFilter.toLowerCase())) {
          return false;
        }
      }

      if (selectedClubs.length > 0) {
        if (!selectedClubs.includes(participant.Utover.Klubb.Navn)) {
          return false;
        }
      }

      if (selectedTeams.length > 0) {
        const teamName = participant.Utover.Team?.Navn;
        if (!teamName || !selectedTeams.includes(teamName)) {
          return false;
        }
      }

      if (selectedClasses.length > 0) {
        if (!selectedClasses.includes(participant.Klasse.Navn)) {
          return false;
        }
      }

      if (startTimeFrom || startTimeTo) {
        const startTime = findAttribute(participant.EtappeDeltaker, 'StarttidFormatert');
        if (startTime) {
          const participantTime = startTime.substring(0, 5);
          if (startTimeFrom && participantTime < startTimeFrom) {
            return false;
          }
          if (startTimeTo && participantTime > startTimeTo) {
            return false;
          }
        }
      }

      return true;
    });
  };

  const renderParticipantGrid = () => {
    const filteredParticipants = getFilteredParticipants();
    const participantPairs: (Participant | undefined)[][] = [];
    
    for (let i = 0; i < filteredParticipants.length; i += 2) {
      participantPairs.push([
        filteredParticipants[i],
        filteredParticipants[i + 1] // undefined if odd number of participants
      ]);
    }
    
    const pages: (Participant | undefined)[][][] = [];
    for (let i = 0; i < participantPairs.length; i += 6) {
      pages.push(participantPairs.slice(i, i + 6));
    }
    
    return pages.map((page, pageIndex) => (
      <div 
        key={pageIndex} 
        style={{ 
          pageBreakAfter: pages.length > 1 && pageIndex < pages.length - 1 ? 'always' : 'auto',
          width: '210mm', // A4 width
          margin: '0 auto',
          padding: '5mm 2mm 2mm 2mm', // Reduced from 12mm to 5mm to fix blank first page
          boxSizing: 'border-box'
        }}
        className="print-page"
      >
        {/* Print-only header with event name and date */}
        <div className="print-header">
          <div style={{
            textAlign: 'center',
            marginBottom: '2mm',
            paddingBottom: '1mm',
            borderBottom: '1px solid #333',
            fontSize: '16px',
            fontWeight: 'bold'
          }}>
            {selectedEvent?.Name} - {selectedEvent ? new Date(selectedEvent.Date).toLocaleDateString('no-NO') : ''}
          </div>
        </div>
        
        {page.map((rowParticipants, rowIndex) => 
          renderRow(pageIndex * 6 + rowIndex, rowParticipants)
        )}
      </div>
    ));
  };

  const renderRow = (rowIndex: number, rowParticipants: (Participant | undefined)[]) => (
    <div 
      key={rowIndex} 
      style={{ 
        marginBottom: '1mm',
        border: '1px solid #333',
        backgroundColor: 'white',
      }}
    >
      <table 
        style={{ 
          width: '100%', 
          borderCollapse: 'collapse',
          minHeight: '45mm',
        }}
      >
        <tbody>
          <tr>
            <td style={{ 
              borderTop: '1px solid #333',
              borderLeft: '1px solid #333',
              borderBottom: '1px solid #333',
              padding: '2mm', 
              textAlign: 'center',
              verticalAlign: 'top',
              width: '7%'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {renderBibBox(rowParticipants[0])}
                <div style={{ marginTop: '3mm' }}>
                  <ShootingDirectionSelector participant={rowParticipants[0]} />
                </div>
                <div
                  style={{
                    width: '24mm',
                    height: '12mm',
                    border: '1px solid #333',
                    marginTop: '3mm',
                    backgroundColor: 'white',
                  }}
                />
              </div>
            </td>
            <td style={{ 
              borderTop: '1px solid #333',
              borderLeft: '1px solid #333',
              borderRight: '1px solid #333',
              borderBottom: '1px solid #333',
              padding: '2mm', 
              textAlign: 'center',
              verticalAlign: 'middle',
              width: '43%'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {renderParticipantInfo(rowParticipants[0])}
                <Space size={10}>
                  {renderWheel()}
                  {renderWheel()}
                </Space>
              </div>
            </td>
            <td style={{ 
              borderTop: '1px solid #333',
              borderRight: '1px solid #333',
              borderBottom: '1px solid #333',
              padding: '2mm', 
              textAlign: 'center',
              verticalAlign: 'middle',
              width: '43%'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {renderParticipantInfo(rowParticipants[1])}
                <Space size={10}>
                  {renderWheel()}
                  {renderWheel()}
                </Space>
              </div>
            </td>
            <td style={{ 
              borderTop: '1px solid #333',
              borderBottom: '1px solid #333',
              padding: '2mm', 
              textAlign: 'center',
              verticalAlign: 'top',
              width: '7%'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {renderBibBox(rowParticipants[1])}
                <div style={{ marginTop: '3mm' }}>
                  <ShootingDirectionSelector participant={rowParticipants[1]} />
                </div>
                <div
                  style={{
                    width: '24mm',
                    height: '12mm',
                    border: '1px solid #333',
                    marginTop: '3mm',
                    backgroundColor: 'white',
                  }}
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="App">
      <style>
        {`
          @media print {
            .print-header {
              display: block !important;
            }
          }
          @media screen {
            .print-header {
              display: none !important;
            }
          }
        `}
      </style>
      <header className="App-header">
        <Title level={1}>Skyteplott</Title>
        
        {!selectedEvent && (
          <Card
            style={{
              backgroundColor: 'white',
              margin: '20px',
              maxWidth: 600,
              width: '100%',
            }}
            size="small"
          >
            <Text type="secondary" style={{ fontStyle: 'italic' }}>
              No biathlon event selected. Go to the Home page to select an event.
            </Text>
          </Card>
        )}

        {selectedEvent && (
          <>
            <Card
              title={`Event: ${selectedEvent.Name}`}
              style={{
                backgroundColor: 'white',
                margin: '20px',
                maxWidth: 1200,
                width: '100%',
              }}
              size="small"
            >
              <Text>
                {selectedEvent.Dicipline.Name} • {new Date(selectedEvent.Date).toLocaleDateString('no-NO')} • {selectedEvent.City.Name}
              </Text>
            </Card>

            {!loading && !error && (
              <div style={{ marginBottom: '20px', textAlign: 'right', paddingRight: '20px' }}>
                <Space>
                  <Button 
                    icon={<FilterOutlined />} 
                    onClick={() => setFilterModalVisible(true)}
                    className="filter-button"
                  >
                    Filter
                  </Button>
                  <Button 
                    icon={<SettingOutlined />} 
                    onClick={() => setSettingsModalVisible(true)}
                    className="settings-button"
                  >
                    Settings
                  </Button>
                  <Button 
                    type="primary" 
                    icon={<PrinterOutlined />} 
                    onClick={handlePrint}
                    className="print-button"
                  >
                    Print Report
                  </Button>
                </Space>
              </div>
            )}

            <Card
              title="Participant Grid"
              style={{
                backgroundColor: 'white',
                margin: '20px',
                maxWidth: 1200,
                width: '100%',
              }}
            >
              {loading ? (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  <Spin size="large" />
                  <Text style={{ display: 'block', marginTop: '10px' }}>Loading participant data...</Text>
                </div>
              ) : error ? (
                <Alert message="Error" description={error} type="error" showIcon />
              ) : (
                <div style={{ padding: '0 20px' }}>
                  {renderParticipantGrid()}
                </div>
              )}
            </Card>
          </>
        )}

        <Modal
          title="Filter Participants"
          open={filterModalVisible}
          onOk={handleFilterOk}
          onCancel={handleFilterCancel}
          width={600}
          footer={[
            <Button key="reset" onClick={handleFilterReset}>
              Reset Filters
            </Button>,
            <Button key="cancel" onClick={handleFilterCancel}>
              Cancel
            </Button>,
            <Button key="ok" type="primary" onClick={handleFilterOk}>
              Apply Filters
            </Button>,
          ]}
        >
          <Form layout="vertical">
            <Form.Item label="Search by Name">
              <Input 
                placeholder="Enter participant name" 
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
              />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Filter by Clubs">
                  <Select
                    mode="multiple"
                    placeholder="Select clubs"
                    allowClear
                    style={{ width: '100%' }}
                    value={selectedClubs}
                    onChange={setSelectedClubs}
                  >
                    {clubs.map(club => (
                      <Select.Option key={club} value={club}>
                        {club}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Filter by Teams">
                  <Select
                    mode="multiple"
                    placeholder="Select teams"
                    allowClear
                    style={{ width: '100%' }}
                    value={selectedTeams}
                    onChange={setSelectedTeams}
                  >
                    {teams.map(team => (
                      <Select.Option key={team} value={team}>
                        {team}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label="Filter by Classes">
              <Select
                mode="multiple"
                placeholder="Select classes"
                allowClear
                style={{ width: '100%' }}
                value={selectedClasses}
                onChange={setSelectedClasses}
              >
                {classes.map(classItem => (
                  <Select.Option key={classItem} value={classItem}>
                    {classItem}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Start Time From">
              <TimePicker
                value={startTimeFrom ? dayjs(startTimeFrom, 'HH:mm') : null}
                onChange={(time) => setStartTimeFrom(time ? time.format('HH:mm') : '')}
                format="HH:mm"
                placeholder="Select start time"
                allowClear
              />
            </Form.Item>
            <Form.Item label="Start Time To">
              <TimePicker
                value={startTimeTo ? dayjs(startTimeTo, 'HH:mm') : null}
                onChange={(time) => setStartTimeTo(time ? time.format('HH:mm') : '')}
                format="HH:mm"
                placeholder="Select end time"
                allowClear
              />
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title="Settings"
          open={settingsModalVisible}
          onOk={handleSettingsOk}
          onCancel={handleSettingsCancel}
          width={500}
        >
          <Form layout="vertical" style={{ padding: '20px' }}>
            <Form.Item label="Skyteretning">
              <Row gutter={16}>
                <Col>
                  <Button 
                    icon={<DownloadOutlined />} 
                    onClick={handleExportShootingDirections}
                    className="export-button"
                  >
                    Export
                  </Button>
                </Col>
                <Col>
                  <Upload
                    accept=".json"
                    beforeUpload={handleImportShootingDirections}
                    showUploadList={false}
                  >
                    <Button 
                      icon={<UploadOutlined />}
                      className="import-button"
                    >
                      Import
                    </Button>
                  </Upload>
                </Col>
              </Row>
            </Form.Item>
          </Form>
        </Modal>
      </header>
    </div>
  );
};

export default Skyteplott;
